import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useHandleActionItem } from "../../../hooks/useHandleActionItem.js";
import InputText from "../../../components/forms/InputText.jsx";
import { getApiUrl } from "../../../config/config.js";

const Toast = Swal.mixin({
	toast: true,
	position: "top-end",
	showConfirmButton: false,
	timer: 3000,
	timerProgressBar: true,
	didOpen: (toast) => {
		toast.onmouseenter = Swal.stopTimer;
		toast.onmouseleave = Swal.resumeTimer;
	},
});

const reverseTranslateKeys = (data) => {
	const reverseMap = {
		Símbolo: "symbol",
		Tipo: "type",
		"Estado de Posición": "positionStatus",
		Mensaje: "message",
		"Stop Loss": "stopLoss",
		Precio: "price",
		"Take Profit": "takeProfit",
		Temporalidad: "timeFrame",
		Estado: "status",
		Imagen: "image",
		Tiempo: "time",
		"Tiempo Cierre": "closeTime",
		"Código App": "appCode",
		id: "id",
	};

	const normalized = {};
	for (const key in data) {
		const trimmedKey = key.trim();
		const normalizedKey = reverseMap[trimmedKey] || trimmedKey;
		normalized[normalizedKey] = (data[key] || "").trim?.() ?? data[key];
	}
	return normalized;
};

const formatDateForInput = (isoString) => {
	if (!isoString) return "";
	try {
		const date = new Date(isoString);
		const year = date.getFullYear();
		const month = String(date.getMonth() + 1).padStart(2, "0");
		const day = String(date.getDate()).padStart(2, "0");
		const hours = String(date.getHours()).padStart(2, "0");
		const minutes = String(date.getMinutes()).padStart(2, "0");
		return `${year}-${month}-${day}T${hours}:${minutes}`;
	} catch {
		return "";
	}
};

// Opciones para los campos de selección
const typeOptions = ["Buy Market", "Buy Limit", "Buy Stop", "Sell Market", "Sell Limit", "Sell Stop"];
const positionStatusOptions = ["opened", "closed", "win", "loss"];
const timeFrameOptions = ["1M", "3M", "5M", "15M", "30M", "1H", "4H", "1D"];
const statusOptions = ["newSignal", "history"];

function AppIbSignalsDetails() {
	const location = useLocation();
	const navigate = useNavigate();
	const locationType = location.state?.type || "";
	const data = useMemo(() => location.state?.data || [], [location.state?.data]);

	const [signal, setSignal] = useState({});
	const { handleDelete, handleSubmit } = useHandleActionItem();

	useEffect(() => {
		if (locationType === "edit" && data) {
			setSignal(reverseTranslateKeys(data));
		}
	}, [locationType, data]);

	const validateForm = () => {
		if (!signal.symbol || !signal.type || !signal.positionStatus || !signal.price || !signal.timeFrame || !signal.status) {
			Toast.fire({
				icon: "error",
				title: "Los campos obligatorios no pueden estar vacíos",
				background: "#1e2836",
			});
			return false;
		}
		return true;
	};

	const handleInputChange = (field, value) => {
		setSignal((prev) => ({ ...prev, [field]: value }));
	};

	// Formatear la fecha actual para el campo time
	const formatCurrentDate = () => {
		const now = new Date();
		return now.toISOString().slice(0, 19).replace("T", " ");
	};

	const handleSubmitForm = async (event) => {
		event.preventDefault();
		if (!validateForm()) return;

		try {
			// Preparar datos para envío
			const signalData = { ...signal };

			// Si es una nueva señal, establecer la fecha actual
			if (locationType === "add") {
				signalData.time = formatCurrentDate();
				// Generar appCode si es necesario
				if (!signalData.appCode) {
					const timestamp = Date.now();
					signalData.appCode = `${timestamp}-${signalData.price}`;
				}
			}

			await handleSubmit(locationType === "add" ? "add" : "update", "signals", signalData);
			navigate("/appIbSignals");
		} catch (error) {
			console.log("ERROR", error);
			Toast.fire({
				icon: "error",
				title: "Error al guardar la señal",
				background: "#1e2836",
			});
		}
	};

	const handleDeleteForm = async (event) => {
		event.preventDefault();
		await handleDelete({ id: signal.id }, "signals", true);
		navigate("/appIbSignals");
	};

	return (
		<div>
			<form onSubmit={handleSubmitForm}>
				<h1 className="text-center">{locationType === "add" ? "Agregar Señal" : "Actualizar Señal"}</h1>

				<InputText label="Símbolo" value={signal.symbol || ""} setValue={(v) => handleInputChange("symbol", v)} />

				<div className="mb-3">
					<label className="form-label">
						Tipo <span className="text-danger">*</span>
					</label>
					<select
						className="form-select bg-white bg-opacity-5"
						value={signal.type || ""}
						onChange={(e) => handleInputChange("type", e.target.value)}>
						<option value="">Seleccionar tipo</option>
						{typeOptions.map((option) => (
							<option key={option} value={option}>
								{option}
							</option>
						))}
					</select>
				</div>

				<div className="mb-3">
					<label className="form-label">
						Estado de Posición <span className="text-danger">*</span>
					</label>
					<select
						className="form-select bg-white bg-opacity-5"
						value={signal.positionStatus || ""}
						onChange={(e) => handleInputChange("positionStatus", e.target.value)}>
						<option value="">Seleccionar estado de posición</option>
						{positionStatusOptions.map((option) => (
							<option key={option} value={option}>
								{option}
							</option>
						))}
					</select>
				</div>

				<InputText label="Mensaje" value={signal.message || ""} setValue={(v) => handleInputChange("message", v)} />

				<InputText label="Stop Loss" type="number" value={signal.stopLoss || ""} setValue={(v) => handleInputChange("stopLoss", v)} />

				<InputText label="Precio" type="number" value={signal.price || ""} setValue={(v) => handleInputChange("price", v)} />

				<InputText label="Take Profit" type="number" value={signal.takeProfit || ""} setValue={(v) => handleInputChange("takeProfit", v)} />

				<div className="mb-3">
					<label className="form-label">
						Temporalidad <span className="text-danger">*</span>
					</label>
					<select
						className="form-select bg-white bg-opacity-5"
						value={signal.timeFrame || ""}
						onChange={(e) => handleInputChange("timeFrame", e.target.value)}>
						<option value="">Seleccionar marco de tiempo</option>
						{timeFrameOptions.map((option) => (
							<option key={option} value={option}>
								{option}
							</option>
						))}
					</select>
				</div>

				<div className="mb-3">
					<label className="form-label">
						Estado <span className="text-danger">*</span>
					</label>
					<select
						className="form-select bg-white bg-opacity-5"
						value={signal.status || ""}
						onChange={(e) => handleInputChange("status", e.target.value)}>
						<option value="">Seleccionar estado</option>
						{statusOptions.map((option) => (
							<option key={option} value={option}>
								{option}
							</option>
						))}
					</select>
				</div>

				{locationType === "edit" && (
					<>
						<InputText
							label="Tiempo"
							type="datetime-local"
							value={formatDateForInput(signal.time)}
							setValue={(v) => handleInputChange("time", v.replace("T", " "))}
							disabled={true}
						/>

						<InputText
							label="Tiempo Cierre"
							type="datetime-local"
							value={formatDateForInput(signal.closeTime)}
							setValue={(v) => handleInputChange("closeTime", v.replace("T", " "))}
						/>

						<InputText
							label="Código de App"
							value={signal.appCode || ""}
							setValue={(v) => handleInputChange("appCode", v)}
							disabled={true}
						/>
					</>
				)}

				<hr />
				<div className="mb-3 d-flex justify-content-around ">
					<button type="submit" className="btn btn-outline-theme btn-lg">
						{locationType === "add" ? "Agregar" : "Actualizar"}
					</button>
					<button onClick={() => navigate("/appIbSignals")} className="btn btn-outline-theme btn-lg">
						Atrás
					</button>
					{locationType === "edit" && (
						<button onClick={handleDeleteForm} className="btn btn-outline-danger btn-lg">
							Eliminar
						</button>
					)}
				</div>
			</form>
		</div>
	);
}

export default AppIbSignalsDetails;
